import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../config/firebasedeconfig";
import "../styles/components//MovieReviewsCarousel.scss";
import { useLanguage } from "../contexts/LanguageContext";

const FALLBACK_POSTER = "https://zupimages.net/up/23/13/yd61.jpg";
const AUTO_SCROLL_MS = 3500;
const SCROLL_STEP = 280;

const getPosterUrl = (posterPath) => {
    if (!posterPath) return FALLBACK_POSTER;
    if (String(posterPath).startsWith("http")) return posterPath;
    return `https://image.tmdb.org/t/p/w342${posterPath}`;
};

const getMovieId = (review) => {
    return review?.tmdbId || review?.movieRatingKey || review?.id || "";
};

const truncateText = (value = "", maxLength = 110) => {
    const text = String(value || "").trim();
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).trim()}…`;
};

/**
 * Carrousel horizontal des avis MovieFrenzy d'un utilisateur.
 *
 * Props :
 * - userId (string) : charge users/{userId}/movieReviews
 * - reviews (array, optionnel) : data locale pour tests sans Firestore
 * - title (string)
 * - emptyLabel (string)
 * - autoScroll (boolean, défaut true)
 * - className (string, optionnel)
 */
const MovieReviewsCarousel = ({
    userId,
    reviews: reviewsFromProps = null,
    title,
    emptyLabel,
    autoScroll = true,
    className = "",
}) => {
    const { t } = useLanguage();

    const carouselTitle =
        title || t("movieReviewsCarousel.title");

    const carouselEmptyLabel =
        emptyLabel || t("movieReviewsCarousel.empty");
    const [reviewsFromFirestore, setReviewsFromFirestore] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const trackRef = useRef(null);
    const isPausedRef = useRef(false);
    const useLocalReviews = Array.isArray(reviewsFromProps);

    useEffect(() => {
        if (useLocalReviews || !userId) {
            setReviewsFromFirestore([]);
            setIsLoading(false);
            setHasError(false);
            return undefined;
        }

        let isMounted = true;

        const loadReviews = async () => {
            setIsLoading(true);
            setHasError(false);

            try {
                const reviewsQuery = query(
                    collection(db, "users", userId, "movieReviews"),
                    orderBy("updatedAt", "desc")
                );

                const snapshot = await getDocs(reviewsQuery);

                if (!isMounted) return;

                const nextReviews = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));

                setReviewsFromFirestore(nextReviews);
            } catch (error) {
                console.error(
                    "[MovieFrenzy] Impossible de charger les avis du carrousel :",
                    error
                );

                if (isMounted) {
                    setReviewsFromFirestore([]);
                    setHasError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadReviews();

        return () => {
            isMounted = false;
        };
    }, [userId, useLocalReviews]);

    const reviews = useMemo(() => {
        const sourceReviews = useLocalReviews
            ? reviewsFromProps
            : reviewsFromFirestore;

        return sourceReviews.filter(
            (review) => review?.visibleOnProfile !== false
        );
    }, [useLocalReviews, reviewsFromProps, reviewsFromFirestore]);

    useEffect(() => {
        if (!autoScroll || reviews.length < 2) return undefined;

        const trackElement = trackRef.current;
        if (!trackElement) return undefined;

        const intervalId = window.setInterval(() => {
            if (isPausedRef.current || !trackRef.current) return;

            const el = trackRef.current;
            const maxScrollLeft = el.scrollWidth - el.clientWidth;

            if (maxScrollLeft <= 8) return;

            const nextLeft = el.scrollLeft + SCROLL_STEP;

            if (nextLeft >= maxScrollLeft - 4) {
                el.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                el.scrollTo({ left: nextLeft, behavior: "smooth" });
            }
        }, AUTO_SCROLL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [autoScroll, reviews.length]);

    const handleScrollBy = (direction) => {
        if (!trackRef.current) return;

        trackRef.current.scrollBy({
            left: direction * SCROLL_STEP,
            behavior: "smooth",
        });
    };

    const handleMouseEnter = () => {
        isPausedRef.current = true;
    };

    const handleMouseLeave = () => {
        isPausedRef.current = false;
    };

    const rootClassName = [
        "movie-reviews-carousel",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    if (!useLocalReviews && !userId) {
        return null;
    }

    return (
        <section className={rootClassName} aria-label={carouselTitle}>
            <div className="movie-reviews-carousel__header">
                <h3 className="movie-reviews-carousel__title">
                    {carouselTitle}
                </h3>

                {reviews.length > 1 && (
                    <div className="movie-reviews-carousel__arrows">
                        <button
                            type="button"
                            className="movie-reviews-carousel__arrow"
                            onClick={() => handleScrollBy(-1)}
                            aria-label={t("movieReviewsCarousel.previous")}
                        >
                            ‹
                        </button>
                        <button
                            type="button"
                            className="movie-reviews-carousel__arrow"
                            onClick={() => handleScrollBy(1)}
                            aria-label={t("movieReviewsCarousel.next")}
                        >
                            ›
                        </button>
                    </div>
                )}
            </div>

            {isLoading && (
                <p className="movie-reviews-carousel__status">
                    {t("movieReviewsCarousel.loading")}
                </p>
            )}

            {!isLoading && hasError && (
                <p className="movie-reviews-carousel__status movie-reviews-carousel__status--error">
                    {t("movieReviewsCarousel.loadError")}
                </p>
            )}

            {!isLoading && !hasError && reviews.length === 0 && (
                <p className="movie-reviews-carousel__empty">
                    {carouselEmptyLabel}
                </p>
            )}

            {!isLoading && !hasError && reviews.length > 0 && (
                <div
                    className="movie-reviews-carousel__track"
                    ref={trackRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {reviews.map((review) => {
                        const movieId = getMovieId(review);
                        const ratingValue = Number(review.rating);
                        const hasValidRating = Number.isFinite(ratingValue);

                        const forumPostId = review.forumPostId || "";
                        const forumReplyId = review.forumReplyId || "";

                        const movieTitle =
                            review.movieTitle ||
                            t("movieReviewsCarousel.unknownMovie");

                        if (!movieId) return null;

                        const reviewDestination = forumPostId
                            ? {
                                pathname: `/subject/${forumPostId}`,
                                search: forumReplyId
                                    ? `?replyId=${encodeURIComponent(forumReplyId)}`
                                    : "",
                            }
                            : `/ficheFilm/${movieId}`;

                        return (
                            <Link
                                key={review.id || `${movieId}-${forumReplyId || "review"}`}
                                to={reviewDestination}
                                state={
                                    forumReplyId
                                        ? { replyId: forumReplyId }
                                        : undefined
                                }
                                className="movie-reviews-carousel__card"
                                aria-label={t("movieReviewsCarousel.openReview").replace(
                                    "{{title}}",
                                    movieTitle
                                )}
                            >
                                <div className="movie-reviews-carousel__poster-wrap">
                                    <img
                                        className="movie-reviews-carousel__poster"
                                        src={getPosterUrl(review.moviePosterPath)}
                                        alt={
                                            review.movieTitle
                                                ? t("movieReviewsCarousel.posterAlt").replace(
                                                    "{{title}}",
                                                    review.movieTitle
                                                )
                                                : t("movieReviewsCarousel.posterFallbackAlt")
                                        }
                                        loading="lazy"
                                    />

                                    {hasValidRating && (
                                        <span className="movie-reviews-carousel__rating">
                                            {ratingValue}/10
                                        </span>
                                    )}
                                </div>

                                <div className="movie-reviews-carousel__body">
                                    <strong className="movie-reviews-carousel__movie">
                                        {movieTitle}
                                    </strong>

                                    <p className="movie-reviews-carousel__excerpt">
                                        {truncateText(review.reviewExcerpt || review.reviewText)}
                                    </p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default MovieReviewsCarousel;